import React, {useEffect, useState} from 'react';
import Joyride, {Step, CallBackProps} from 'react-joyride';
import {waitForTourTargets} from '../utils/tourChecker';
import {useGlobalTour} from '../utils/useGlobalTour';

import {getDashboardSteps} from '../view/steps/dashboardSteps';
import {getAnalyticsSteps} from '../view/steps/analyticsSteps';
import {getCoursesSteps} from '../view/steps/coursesSteps';
import {getLibrarySteps} from '../view/steps/librarySteps';
import {getMicrolessonsSteps} from '../view/steps/microlessonsSteps';
import {getAiHomeSteps} from '../view/steps/aiHomeSteps';
import '../styles.css';
type Section =
  | 'dashboard'
  | 'analytics'
  | 'courses'
  | 'library'
  | 'microlessons'
  | 'aiHome';

interface Props {
  section: Section;
}

const stepMap: Record<Section, () => Step[]> = {
  dashboard: getDashboardSteps,
  analytics: getAnalyticsSteps,
  courses: getCoursesSteps,
  library: getLibrarySteps,
  microlessons: getMicrolessonsSteps,
  aiHome: getAiHomeSteps,
};

function getValidSteps(steps: Step[]): Step[] {
  return steps.filter(step => {
    if (typeof step.target !== 'string') return true;
    const el = document.querySelector(step.target);
    return el instanceof HTMLElement && el.offsetParent !== null;
  });
}

export default function TourContainer({section}: Props) {
  const {runTour, tourStepIndex, handleJoyrideCallback} = useGlobalTour();
  const [canRun, setCanRun] = useState(false);

  const rawSteps = stepMap[section]();
  const steps = getValidSteps(rawSteps);

  useEffect(() => {
    if (!runTour || steps.length === 0) return;

    const selectors = steps
      .map(step => step.target)
      .filter((target): target is string => typeof target === 'string');

    waitForTourTargets(selectors, 5000)
      .then(() => {
        const allStillExist = selectors.every(
          sel => !!document.querySelector(sel),
        );
        if (allStillExist) {
          setCanRun(true);
        } else {
          console.warn('[TOUR] No visible targets found.');
        }
      })
      .catch(err => {
        console.warn('[TOUR] Target disappeared in step:', err);
      });
  }, [runTour, steps]);

  const handleSafeCallback = (data: CallBackProps) => {
    const {type, step} = data;
    if (
      type === 'step:before' &&
      typeof step.target === 'string' &&
      !document.querySelector(step.target)
    ) {
      console.warn('[TOUR] Target desaparecido en paso:', step.target);
      return;
    }
    handleJoyrideCallback(data);
  };

  if (!canRun) return null;

  return (
    <Joyride
      steps={steps}
      run={canRun}
      stepIndex={tourStepIndex}
      continuous
      showSkipButton
      showProgress
      callback={handleSafeCallback}
      styles={{
        options: {
          zIndex: 10000,
          arrowColor: '#e3ffeb',
          overlayColor: 'rgba(0, 0, 0, 0.8)',
          primaryColor: '#000',
          textColor: '#004a14',
        },
      }}
    />
  );
}
